import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { WHATSAPP_URL, CONTACT_EMAIL } from "@/data/translations";const About = () => {
  const { t, lang, setLang } = useLang();
  useDocumentMeta(lang);

  /* Per-route SEO title & description for /about */
  useEffect(() => {
    const titles = {
      fr: "Qui sommes-nous — Marocanada · Immigration Maroc Canada",
      en: "Who We Are — Marocanada · Morocco to Canada Immigration",
    };
    const descs = {
      fr: "Marocanada — cabinet d'immigration né d'une expérience vécue. Équipe bilingue installée au Canada qui accompagne les Marocains francophones, de l'évaluation CRS à la résidence permanente.",
      en: "Marocanada — an immigration firm born from a lived experience. A bilingual team based in Canada guiding French-speaking Moroccans from CRS assessment to permanent residence.",
    };
    document.title = titles[lang] || titles.fr;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", descs[lang] || descs.fr);
  }, [lang]);

  return (
    <main
      data-testid="about-page"
      className="bg-[#0D1117] text-[#F5F2EE] min-h-screen"
    >
      {/* Mini-header — identical to FAQ */}
      <header className="relative border-b border-[#F5F2EE]/8">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
          <Link
            to="/"
            data-testid="about-logo"
            dir="ltr"
            className="flex items-baseline gap-0 select-none font-display"
          >
            <span className="text-2xl text-[#C8A55E] font-normal tracking-tight">
              Maro
            </span>
            <span className="text-2xl italic text-[#F5F2EE] font-normal tracking-tight">
              canada
            </span>
          </Link>

          <div className="flex items-center gap-5">
            <div className="hidden sm:flex items-center gap-3 text-xs">
              {["fr", "en"].map((l, i) => (
                <div key={l} className="flex items-center gap-3">
                  <button
                    data-testid={`about-lang-${l}`}
                    onClick={() => setLang(l)}
                    className={`font-mono-accent text-[10px] transition-colors ${
                      lang === l
                        ? "text-[#E8A045]"
                        : "text-[#F5F2EE]/40 hover:text-[#F5F2EE]/70"
                    }`}
                  >
                    {l.toUpperCase()}
                  </button>
                  {i === 0 && <span className="block w-px h-3 bg-[#F5F2EE]/15" />}
                </div>
              ))}
            </div>
            <Link
              to="/"
              data-testid="about-back-home"
              className="font-mono-accent text-[10px] text-[#F5F2EE]/65 hover:text-[#E8A045] transition-colors"
            >
              {t.about.back}
            </Link>
          </div>
        </div>
      </header>

      {/* Title */}
      <section className="max-w-[1100px] mx-auto px-6 lg:px-10 pt-20 sm:pt-28 pb-12">
        <span className="brushstroke mb-6" />
        <div className="font-mono-accent text-[10px] text-[#E8A045] mb-5">
          {t.about.eyebrow}
        </div>
        <h1
          data-testid="about-headline"
          className="font-display text-4xl sm:text-5xl lg:text-[3.5rem] text-[#F5F2EE] leading-[1.05] tracking-tight max-w-4xl"
        >
          {t.about.headline}
        </h1>
      </section>

      {/* Sections */}
      <section className="max-w-[1100px] mx-auto px-6 lg:px-10 pb-20">
        <div className="divide-y divide-[#F5F2EE]/10 border-t border-[#F5F2EE]/10">
          {t.about.sections.map((s, i) => (
            <motion.article
              key={i}
              data-testid={`about-section-${i}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className="py-10 sm:py-12 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10"
            >
              <div className="md:col-span-4 flex items-start gap-5">
                <span className="font-mono-accent text-[10px] text-[#E8A045] pt-2 w-8 flex-shrink-0">
                  0{i + 1}
                </span>
                <h2 className="font-display text-2xl sm:text-3xl lg:text-[2rem] text-[#F5F2EE] leading-tight tracking-tight">
                  {s.title}
                </h2>
              </div>
              <div className="md:col-span-8">
                <p className="font-body text-[15px] sm:text-base text-[#F5F2EE]/65 leading-relaxed max-w-3xl">
                  {s.body}
                </p>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#1C1C1E] border-t border-[#F5F2EE]/8">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-16 sm:py-20 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <h3 className="font-display text-3xl sm:text-4xl text-[#F5F2EE] leading-tight tracking-tight">
              {t.about.cta}
            </h3>
          </div>
          <div className="md:col-span-4 flex flex-col gap-3 md:items-end">
            <a
              href="/#contact"
              data-testid="about-cta-button"
              className="inner-border-btn group inline-flex items-center justify-center gap-3 bg-[#2FBFAD] hover:bg-[#239B8C] text-[#0D1117] px-7 py-3.5 rounded-full font-mono-accent text-[11px] font-medium whitespace-nowrap"
            >
              <span>{t.about.cta}</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={2} />
            </a>
            <div className="flex items-center gap-4 text-xs">
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="about-whatsapp" className="font-mono-accent text-[10px] text-[#F5F2EE]/65 hover:text-[#E8A045] transition-colors">
                WhatsApp
              </a>
              <span className="w-px h-3 bg-[#F5F2EE]/15" />
              <a href={`mailto:${CONTACT_EMAIL}`} data-testid="about-email" className="font-mono-accent text-[10px] text-[#F5F2EE]/65 hover:text-[#E8A045] transition-colors">
                {CONTACT_EMAIL}
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default About;
